import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bell,
  CalendarDays,
  CheckCircle2,
  Clock,
  CreditCard,
  DollarSign,
  RefreshCw,
  WalletCards,
} from "lucide-react";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useAuth } from "@/context/AuthContext";
import UserProfileMenu from "@/components/UserProfileMenu";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";

const TIME_ZONE = "America/Cuiaba";

const FINANCE_BAR_COLORS = ["#14B8A6", "#3B82F6", "#F59E0B", "#8B5CF6", "#EF4444", "#22C55E", "#06B6D4"];
const FINANCE_PIE_COLORS = ["#0EA5E9", "#10B981", "#F59E0B", "#A855F7", "#EF4444", "#84CC16", "#F97316", "#6366F1"];
const CHART_TOOLTIP_STYLE = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: "12px",
  boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
};

const money = (value) =>
  Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const normalizeText = (value = "") =>
  value
    .toString()
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const getCuiabaTodayDate = () => {
  const parts = new Intl.DateTimeFormat("pt-BR", {
    timeZone: TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const day = Number(parts.find((p) => p.type === "day")?.value);
  const month = Number(parts.find((p) => p.type === "month")?.value);
  const year = Number(parts.find((p) => p.type === "year")?.value);
  const date = new Date(year, month - 1, day);
  date.setHours(0, 0, 0, 0);
  return date;
};

const today = getCuiabaTodayDate();

const addMonths = (date, months) => {
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  next.setMonth(next.getMonth() + Number(months || 0));
  next.setHours(23, 59, 59, 999);
  return next;
};

const parseDate = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const date = new Date(value.getFullYear(), value.getMonth(), value.getDate());
    date.setHours(0, 0, 0, 0);
    return date;
  }

  const text = value.toString().trim();
  let match = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const [, year, month, day] = match;
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    date.setHours(0, 0, 0, 0);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  match = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (match) {
    const [, day, month, year] = match;
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    date.setHours(0, 0, 0, 0);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  return null;
};

const formatDate = (value) => {
  if (!value) return "Não informado";
  const text = value.toString().trim();
  const br = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (br) return text;
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) {
    const [, year, month, day] = iso;
    return `${day}/${month}/${year}`;
  }
  const date = parseDate(value);
  return date ? new Intl.DateTimeFormat("pt-BR", { timeZone: TIME_ZONE }).format(date) : "Não informado";
};

const parseMoneyValue = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const normalized = value
    .toString()
    .trim()
    .replace(/R\$\s?/g, "")
    .replace(/\./g, "")
    .replace(",", ".");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const firstMoney = (...values) => {
  for (const value of values) {
    const parsed = parseMoneyValue(value);
    if (parsed !== null) return parsed;
  }
  return 0;
};

const isPaidExpense = (expense) => normalizeText(expense.status).includes("pago");

const isInstallmentExpense = (expense) => {
  const installments = parseMoneyValue(expense.installments);
  return normalizeText(expense.payment_type).includes("credito") && installments && installments > 1;
};

const getExpenseInstallmentAmount = (expense) => {
  if (!isInstallmentExpense(expense)) return 0;
  const total = parseMoneyValue(expense.total_value);
  const installments = parseMoneyValue(expense.installments);
  const monthly = parseMoneyValue(expense.monthly_installment);
  const value = parseMoneyValue(expense.value);

  if (monthly !== null && monthly > 0) return monthly;
  if (value !== null && value > 0) return value;
  if (total !== null && installments && installments > 0) return total / installments;
  return 0;
};

const getExpensePayableAmount = (expense) => {
  if (isInstallmentExpense(expense)) return getExpenseInstallmentAmount(expense);
  return firstMoney(expense.total_value, expense.value, expense.monthly_installment);
};

const getExpenseProvisionedAmount = (expense) => firstMoney(expense.total_value, expense.value, expense.monthly_installment);
const getExpenseMonthlyAmount = (expense) => getExpenseInstallmentAmount(expense);

const toExpenseDate = (expense) => {
  const directDate = parseDate(expense.due_date || expense.payment_date || expense.reminder_date);
  if (directDate) return directDate;
  const year = Number(expense.year) || today.getFullYear();
  const month = Math.min(Math.max(Number(expense.month) || today.getMonth() + 1, 1), 12);
  const day = Math.min(Math.max(Number(expense.day) || 1, 1), 31);
  const date = new Date(year, month - 1, day);
  date.setHours(0, 0, 0, 0);
  return date;
};

const getPaymentState = (expense) => {
  const status = normalizeText(expense.status);
  const paid = status.includes("pago");
  const pending = status.includes("pendente");
  const due = toExpenseDate(expense);
  const reminderDate = parseDate(expense.reminder_date);

  if (paid) return { label: "Pago", tone: "success" };
  if (due < today) return { label: "Vencida", tone: "danger" };
  if (due.getTime() === today.getTime()) return { label: "Vence hoje", tone: "warning" };
  if (reminderDate && reminderDate <= today) return { label: "Lembrete ativo", tone: "warning" };
  if (pending) return { label: "Pendente", tone: "warning" };
  return { label: "A vencer", tone: "info" };
};

const StatCard = ({ title, value, icon: Icon, tone = "primary" }) => {
  const toneClass = {
    primary: "bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/20",
    blue: "bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/20",
    warning: "bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-lg shadow-amber-500/20",
    success: "bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-500/20",
    danger: "bg-gradient-to-br from-rose-500 to-red-600 text-white shadow-lg shadow-rose-500/20",
  }[tone];

  return (
    <div className="bg-white border border-border rounded-2xl p-6 flex items-center justify-between shadow-sm">
      <div>
        <div className="text-sm text-muted-foreground mb-1">{title}</div>
        <div className="text-2xl md:text-3xl font-display font-semibold text-primary">{value}</div>
      </div>
      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${toneClass}`}>
        <Icon className="w-6 h-6" />
      </div>
    </div>
  );
};

const FinancePanel = () => {
  const { user } = useAuth();
  const [finance, setFinance] = useState({ fixed_expenses: [] });
  const [loading, setLoading] = useState(true);
  const [selectedMonths, setSelectedMonths] = useState(12);
  const expenses = useMemo(() => finance?.fixed_expenses || [], [finance]);

  const loadFinance = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/finance");
      setFinance(data || { fixed_expenses: [] });
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Erro ao carregar painel financeiro");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFinance();
  }, [loadFinance]);

  const dashboard = useMemo(() => {
    const sorted = [...expenses].sort((a, b) => toExpenseDate(a) - toExpenseDate(b));
    const periodEnd = addMonths(today, selectedMonths);
    const periodExpenses = sorted.filter((e) => toExpenseDate(e) <= periodEnd);
    const paid = periodExpenses.filter(isPaidExpense);
    const open = periodExpenses.filter((e) => !isPaidExpense(e));
    const totalGeneral = periodExpenses.reduce((sum, e) => sum + getExpenseProvisionedAmount(e), 0);
    const paidTotal = paid.reduce((sum, e) => sum + getExpenseProvisionedAmount(e), 0);
    const provisionedTotal = open.reduce((sum, e) => sum + getExpensePayableAmount(e), 0);
    const monthlyTotal = open.reduce((sum, e) => sum + getExpenseMonthlyAmount(e), 0);

    const byMonth = open.reduce((acc, e) => {
      const date = toExpenseDate(e);
      const label = new Intl.DateTimeFormat("pt-BR", { timeZone: TIME_ZONE, month: "2-digit", year: "numeric" }).format(date);
      acc[label] = (acc[label] || 0) + getExpensePayableAmount(e);
      return acc;
    }, {});

    const byCompany = open.reduce((acc, e) => {
      const label = e.company || "Sem empresa";
      acc[label] = (acc[label] || 0) + getExpensePayableAmount(e);
      return acc;
    }, {});

    return {
      totalGeneral,
      paidTotal,
      provisionedTotal,
      monthlyTotal,
      paidCount: paid.length,
      openCount: open.length,
      periodExpenses,
      sorted,
      byMonth: Object.entries(byMonth).map(([name, value]) => ({ name, value })),
      byCompany: Object.entries(byCompany).map(([name, value]) => ({ name, value })),
    };
  }, [expenses, selectedMonths]);

  const reminders = dashboard.sorted
    .filter((e) => !isPaidExpense(e))
    .map((expense) => ({ expense, state: getPaymentState(expense), due: toExpenseDate(expense) }))
    .sort((a, b) => a.due - b.due);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="finance-panel">
      <header className="bg-white border-b border-border">
        <div className="px-6 lg:px-10 py-5 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-sm text-muted-foreground">Boa tarde, {user?.name || user?.email}.</div>
            <h1 className="text-3xl font-display font-semibold text-primary mt-6">Painel Financeiro</h1>
            <p className="text-muted-foreground mt-1">Resumo de despesas fixas, vencimentos, parcelas e lembretes de pagamento. Datas em pt-BR · Cuiabá-MT.</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap justify-end">
            <button onClick={loadFinance} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-border bg-white hover:bg-secondary/40 text-sm font-medium">
              <RefreshCw className="w-4 h-4" /> Atualizar
            </button>
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-border bg-white text-sm">
              <span className="text-muted-foreground">Período</span>
              <select value={selectedMonths} onChange={(e) => setSelectedMonths(Number(e.target.value))} className="bg-transparent outline-none font-medium text-primary">
                <option value={6}>6 meses</option>
                <option value={12}>12 meses</option>
                <option value={18}>18 meses</option>
                <option value={24}>24 meses</option>
                <option value={36}>3 anos</option>
                <option value={48}>4 anos</option>
                <option value={60}>5 anos</option>
              </select>
            </div>
            {user?.role === "admin" && <Link to="/admin" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90">Admin</Link>}
            <UserProfileMenu />
          </div>
        </div>
      </header>

      <main className="px-6 lg:px-10 py-8 space-y-8">
        {loading ? (
          <div className="bg-white border border-border rounded-2xl p-10 text-center text-muted-foreground">Carregando dados financeiros…</div>
        ) : (
          <>
            <section className="grid md:grid-cols-2 xl:grid-cols-5 gap-5">
              <StatCard title="Lançamentos no período" value={dashboard.periodExpenses.length} icon={WalletCards} tone="primary" />
              <StatCard title="Total provisionado" value={money(dashboard.provisionedTotal)} icon={DollarSign} tone="warning" />
              <StatCard title="Total Geral" value={money(dashboard.totalGeneral)} icon={DollarSign} tone="blue" />
              <StatCard title="Mensal parcelado" value={money(dashboard.monthlyTotal)} icon={CreditCard} tone="primary" />
              <StatCard title="Total pago" value={money(dashboard.paidTotal)} icon={CheckCircle2} tone="success" />
            </section>

            <section className="grid xl:grid-cols-2 gap-6">
              <div className="bg-white border border-border rounded-2xl p-6 shadow-sm min-h-[360px]">
                <h2 className="font-display text-xl text-primary mb-6">Despesas por mês</h2>
                {dashboard.byMonth.length ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={dashboard.byMonth}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(value) => money(value)} contentStyle={CHART_TOOLTIP_STYLE} />
                      <Bar dataKey="value" name="Despesas a pagar" radius={[10, 10, 0, 0]}>
                        {dashboard.byMonth.map((_, index) => (
                          <Cell key={index} fill={FINANCE_BAR_COLORS[index % FINANCE_BAR_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[260px] flex items-center justify-center text-muted-foreground">Nenhuma despesa cadastrada no período.</div>
                )}
              </div>

              <div className="bg-white border border-border rounded-2xl p-6 shadow-sm min-h-[360px]">
                <h2 className="font-display text-xl text-primary mb-6">Despesas por empresa</h2>
                {dashboard.byCompany.length ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={dashboard.byCompany} dataKey="value" nameKey="name" innerRadius={70} outerRadius={105} paddingAngle={4}>
                        {dashboard.byCompany.map((_, index) => (
                          <Cell key={index} fill={FINANCE_PIE_COLORS[index % FINANCE_PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => money(value)} contentStyle={CHART_TOOLTIP_STYLE} />
                      <Legend iconType="circle" wrapperStyle={{ paddingTop: "12px" }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[260px] flex items-center justify-center text-muted-foreground">Nenhuma empresa com valor em aberto no período.</div>
                )}
              </div>
            </section>

            <section className="bg-white border border-border rounded-2xl p-6 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <div>
                  <h2 className="font-display text-xl text-primary">Agenda de pagamentos</h2>
                  <p className="text-sm text-muted-foreground">Pagamentos pendentes e a vencer, com data de vencimento e lembrete.</p>
                </div>
                <Bell className="w-6 h-6 text-accent" />
              </div>

              {reminders.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-muted-foreground border-b border-border">
                        <th className="py-3 pr-4">Despesa</th>
                        <th className="py-3 pr-4">Empresa</th>
                        <th className="py-3 pr-4">Pagamento</th>
                        <th className="py-3 pr-4">Vencimento</th>
                        <th className="py-3 pr-4">Lembrete</th>
                        <th className="py-3 pr-4">Parcela</th>
                        <th className="py-3 pr-4">Valor</th>
                        <th className="py-3 pr-4">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reminders.map(({ expense, state, due }, index) => (
                        <tr key={`${expense.name}-${index}`} className="border-b border-border/60 last:border-0">
                          <td className="py-3 pr-4 font-medium text-primary">{expense.name || "Despesa sem nome"}</td>
                          <td className="py-3 pr-4">{expense.company || "-"}</td>
                          <td className="py-3 pr-4">{expense.payment_type || "-"}</td>
                          <td className="py-3 pr-4 inline-flex items-center gap-2"><CalendarDays className="w-4 h-4 text-muted-foreground" /> {formatDate(due)}</td>
                          <td className="py-3 pr-4">{formatDate(expense.reminder_date)}</td>
                          <td className="py-3 pr-4">{isInstallmentExpense(expense) ? `${expense.installments || "?"}x` : "-"}</td>
                          <td className="py-3 pr-4 font-medium">{money(getExpensePayableAmount(expense))}</td>
                          <td className="py-3 pr-4">
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${state.tone === "danger" ? "bg-red-100 text-red-700" : state.tone === "warning" ? "bg-amber-100 text-amber-700" : state.tone === "success" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>
                              <Clock className="w-3 h-3" /> {state.label}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="py-10 text-center text-muted-foreground">Nenhuma despesa em aberto para lembrar.</div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
};

export default FinancePanel;
